import { jsx as _jsx } from "react/jsx-runtime";
import { autocompletion, closeBrackets, closeBracketsKeymap, completionKeymap, } from '@codemirror/autocomplete';
import { defaultKeymap, history, historyKeymap, indentWithTab } from '@codemirror/commands';
import { bracketMatching, foldGutter, foldKeymap, indentOnInput } from '@codemirror/language';
import { lintKeymap } from '@codemirror/lint';
import { highlightSelectionMatches, searchKeymap } from '@codemirror/search';
import { Compartment, EditorState } from '@codemirror/state';
import { crosshairCursor, drawSelection, dropCursor, EditorView, highlightActiveLine, highlightActiveLineGutter, highlightSpecialChars, keymap, lineNumbers, rectangularSelection, } from '@codemirror/view';
import { useCallback, useEffect, useRef } from 'react';
import { wireScript } from './language/index.js';
import { wireScriptTheme } from './themes/wirescript-theme.js';
// Compartments for dynamic reconfiguration
const themeCompartment = new Compartment();
const readOnlyCompartment = new Compartment();
// Stable default to avoid recreating editor on every render
const DEFAULT_EXTENSIONS = [];
/**
 * CodeMirror 6 editor component for WireScript
 */
export function WireScriptEditor({ value = '', onChange, dark = false, readOnly = false, extensions = DEFAULT_EXTENSIONS, className, style, autoFocus = false, placeholder, }) {
    const containerRef = useRef(null);
    const viewRef = useRef(null);
    const onChangeRef = useRef(onChange);
    const initialValueRef = useRef(value);
    // Keep onChange ref up to date
    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);
    // Create update listener
    const updateListener = useCallback(() => {
        return EditorView.updateListener.of((update) => {
            if (update.docChanged && onChangeRef.current) {
                onChangeRef.current(update.state.doc.toString());
            }
        });
    }, []);
    // Initialize editor (only once on mount)
    useEffect(() => {
        if (!containerRef.current)
            return;
        const state = EditorState.create({
            doc: initialValueRef.current,
            extensions: [
                // Basic setup
                lineNumbers(),
                highlightActiveLineGutter(),
                highlightSpecialChars(),
                history(),
                foldGutter(),
                drawSelection(),
                dropCursor(),
                EditorState.allowMultipleSelections.of(true),
                indentOnInput(),
                bracketMatching(),
                closeBrackets(),
                autocompletion(),
                rectangularSelection(),
                crosshairCursor(),
                highlightActiveLine(),
                highlightSelectionMatches(),
                // Keymaps
                keymap.of([
                    ...closeBracketsKeymap,
                    ...defaultKeymap,
                    ...searchKeymap,
                    ...historyKeymap,
                    ...foldKeymap,
                    ...completionKeymap,
                    ...lintKeymap,
                    indentWithTab,
                ]),
                // WireScript language
                wireScript(),
                // Theme (in compartment for dynamic updates)
                themeCompartment.of(wireScriptTheme(dark)),
                // Read-only mode (in compartment for dynamic updates)
                readOnlyCompartment.of(EditorState.readOnly.of(readOnly)),
                // Change listener
                updateListener(),
                // Placeholder
                placeholder ? EditorView.contentAttributes.of({ 'data-placeholder': placeholder }) : [],
                // User extensions
                ...extensions,
            ],
        });
        const view = new EditorView({
            state,
            parent: containerRef.current,
        });
        viewRef.current = view;
        if (autoFocus) {
            view.focus();
        }
        return () => {
            view.destroy();
            viewRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        autoFocus,
        dark,
        extensions,
        placeholder,
        readOnly, // Change listener
        updateListener,
    ]); // Only run once on mount - value updates handled by separate effect
    // Update content when value prop changes externally
    useEffect(() => {
        const view = viewRef.current;
        if (!view)
            return;
        const currentValue = view.state.doc.toString();
        if (value !== currentValue) {
            view.dispatch({
                changes: {
                    from: 0,
                    to: currentValue.length,
                    insert: value,
                },
            });
        }
    }, [value]);
    // Update theme when dark prop changes
    useEffect(() => {
        const view = viewRef.current;
        if (!view)
            return;
        // Reconfigure theme compartment
        view.dispatch({
            effects: themeCompartment.reconfigure(wireScriptTheme(dark)),
        });
    }, [dark]);
    // Update readOnly when prop changes
    useEffect(() => {
        const view = viewRef.current;
        if (!view)
            return;
        // Reconfigure readOnly compartment
        view.dispatch({
            effects: readOnlyCompartment.reconfigure(EditorState.readOnly.of(readOnly)),
        });
    }, [readOnly]);
    return (_jsx("div", { ref: containerRef, className: className, style: {
            height: '100%',
            overflow: 'auto',
            ...style,
        } }));
}
/**
 * Get the current EditorView instance (for advanced use cases)
 */
export function useEditorView(_ref) {
    // This would need to be connected to the actual view ref
    // For now, this is a placeholder
    return null;
}
